import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  Drawer,
  EmptyState,
  FileUpload,
  Select,
  Spinner,
  Textarea,
  useToast,
} from '../../ui/components';
import { IconButton } from '../../ui/components/Button';
import { Icon } from '../../ui/icons';
import { useT } from '../../i18n';
import { useMaxUploadBytes } from '../../app/PropertyContext';
import { ATTACHMENT_TYPES, attachmentTypeLabel, makeAttachmentsApi } from './attachmentsApi';
import './attachments.css';

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let val = bytes / 1024;
  let i = 0;
  while (val >= 1024 && i < units.length - 1) { val /= 1024; i += 1; }
  return `${val.toFixed(val < 10 ? 1 : 0)} ${units[i]}`;
}

/**
 * Reusable attachments card for a requisition or purchase order. Uploads bytes straight to S3 via
 * a presigned URL, then registers metadata — the same flow as supplier documents.
 *
 * @param basePath  resource attachments path, e.g. `/requisitions/{id}/attachments`
 * @param canManage when true, shows upload/delete controls
 */
export default function AttachmentsCard({ basePath, canManage = false }) {
  const toast = useToast();
  const t = useT();
  const { maxMb } = useMaxUploadBytes();
  const client = useMemo(() => makeAttachmentsApi(basePath), [basePath]);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [type, setType] = useState('Other');
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await client.list());
    } catch (err) {
      toast.error(t('Could not load attachments'), { description: err?.message });
    } finally {
      setLoading(false);
    }
  }, [client, toast, t]);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => { setFiles([]); setType('Other'); setDescription(''); };
  const close = () => { if (!uploading) { setDrawerOpen(false); resetForm(); } };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (files.length === 0) { toast.error(t('Choose a file to upload')); return; }
    setUploading(true);
    try {
      await client.upload(files[0], { attachmentType: type, description: description.trim() || null });
      toast.success(t('Attachment uploaded'));
      setDrawerOpen(false);
      resetForm();
      await load();
    } catch (err) {
      toast.error(t('Upload failed'), { description: err?.message });
    } finally {
      setUploading(false);
    }
  };

  const handleOpen = async (a) => {
    try {
      await client.open(a.id);
    } catch (err) {
      toast.error(t('Could not open attachment'), { description: err?.message });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await client.remove(deleteTarget.id);
      toast.success(t('Attachment removed'));
      setDeleteTarget(null);
      await load();
    } catch (err) {
      toast.error(t('Could not remove attachment'), { description: err?.message });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card>
      <Card.Header>
        <Card.Title>{t('Attachments')}</Card.Title>
        {canManage && (
          <Button variant="ghost" size="sm" leadingIcon={<Icon.Upload size={15} />} onClick={() => setDrawerOpen(true)}>
            {t('Upload')}
          </Button>
        )}
      </Card.Header>
      <Card.Body>
        {loading ? (
          <div className="att-center"><Spinner size={22} /></div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<Icon.FileText size={26} />}
            title={t('No attachments')}
            description={canManage ? t('Attach quotes, specs, photos or invoices for this document.') : t('No files have been attached.')}
          />
        ) : (
          <div className="att-list">
            {items.map((a) => (
              <div key={a.id} className="att-item">
                <div className="att-item__main">
                  <span className="att-item__title">
                    <Icon.FileText size={15} /> {a.fileName}
                    <Badge tone="neutral">{t(attachmentTypeLabel(a.attachmentType))}</Badge>
                  </span>
                  <span className="att-item__meta">
                    <span>{formatBytes(a.sizeBytes)}</span>
                    <span>{t('Uploaded {date}', { date: new Date(a.createdAt).toLocaleDateString() })}</span>
                    {a.description && <span>· {a.description}</span>}
                  </span>
                </div>
                <div className="att-item__actions">
                  <IconButton aria-label={t('Open attachment')} variant="ghost" size="icon-sm" onClick={() => handleOpen(a)}>
                    <Icon.Download size={15} />
                  </IconButton>
                  {canManage && (
                    <IconButton aria-label={t('Delete attachment')} variant="ghost" size="icon-sm" onClick={() => setDeleteTarget(a)}>
                      <Icon.Trash size={15} />
                    </IconButton>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card.Body>

      <Drawer open={drawerOpen} onClose={close} title={t('Upload attachment')} size="md">
        <form className="att-form" onSubmit={handleUpload}>
          <FileUpload
            files={files}
            onChange={setFiles}
            multiple={false}
            maxSizeMB={maxMb}
            hint={t('PDF, image or Office file up to {n} MB', { n: maxMb })}
          />
          <div className="ps-field">
            <span className="ps-field__label">{t('Type')}</span>
            <Select value={type} onChange={setType} options={ATTACHMENT_TYPES.map((o) => ({ ...o, label: t(o.label) }))} ariaLabel={t('Attachment type')} />
          </div>
          <Textarea label={t('Description (optional)')} value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
          <div className="att-form__actions">
            <Button type="button" variant="ghost" onClick={close} disabled={uploading}>{t('Cancel')}</Button>
            <Button type="submit" variant="primary" loading={uploading} disabled={files.length === 0}>{t('Upload')}</Button>
          </div>
        </form>
      </Drawer>

      <ConfirmDialog
        open={!!deleteTarget}
        onCancel={() => (deleting ? null : setDeleteTarget(null))}
        onConfirm={handleDelete}
        title={t('Delete attachment')}
        description={deleteTarget ? t('Delete "{name}"? This permanently removes the file.', { name: deleteTarget.fileName }) : ''}
        confirmLabel={t('Delete')}
        tone="danger"
        loading={deleting}
      />
    </Card>
  );
}
